import React, { useEffect } from 'react';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { NumberField } from 'ui-toolkit/components/NumberField/NumberField';
import { lenderPaidOptions } from 'components/cdBuilder/utilities/lenderPaidOptions';
import { Field, FieldProps, useField } from 'formik';
import { SelectField } from 'ui-toolkit/components/SelectField/SelectField';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';

export interface IBuyerFieldsProps {
  properties: { [key: string]: boolean };
  isRefinance: boolean;
  baseName: string;
  readOnly: boolean;
}

export const BuyerFields: React.FC<IBuyerFieldsProps> = ({ properties, isRefinance, baseName, readOnly }) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const sendingToLabel = isZillowClosingEnabled ? 'Sending to Zillow Closing' : 'Sending to Spruce';

  const [{ value: buyerPaidAmount }, , { setValue }] = useField<number | undefined>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.BuyerPaidAmount}`
  );
  const [{ value: othersPaidBuyer }] = useField<number | undefined>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.OthersPaidBuyer}`
  );
  const [{ value: buyerPaidAtClosing }] = useField<number | undefined>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.BuyerPaidAtClosing}`
  );
  const hasBuyerPaidAmount = properties[eFeePropertyNames.BuyerPaidAmount];
  const hasBuyerPaidAtClosing = properties[eFeePropertyNames.BuyerPaidAtClosing];
  const hasOthersPaidBuyer = properties[eFeePropertyNames.OthersPaidBuyer];

  useEffect(() => {
    if (hasBuyerPaidAmount && hasBuyerPaidAtClosing && hasOthersPaidBuyer) {
      const total = (othersPaidBuyer || 0) + (buyerPaidAtClosing || 0);
      if (total !== buyerPaidAmount) {
        setValue(total);
      }
    }
  }, [
    othersPaidBuyer,
    buyerPaidAmount,
    buyerPaidAtClosing,
    setValue,
    hasBuyerPaidAmount,
    hasBuyerPaidAtClosing,
    hasOthersPaidBuyer,
  ]);

  return (
    <>
      {properties[eFeePropertyNames.BuyerPaidAmount] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6 p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.BuyerPaidAmount}`}
            label={isRefinance ? 'Borrower Paid' : 'Buyer Paid'}
            hintText={properties[eFeePropertyNames.BuyerPaidAtClosing] ? 'Total Amount' : 'At closing'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly || properties[eFeePropertyNames.BuyerPaidAtClosing]}
          />
        </div>
      )}
      {properties[eFeePropertyNames.BuyerPaidAtClosing] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6 p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.BuyerPaidAtClosing}`}
            label={
              (!properties[eFeePropertyNames.BuyerPaidAmount] && (isRefinance ? 'Borrower Paid' : 'Buyer Paid')) || (
                <div>&nbsp;</div>
              )
            }
            hintText={'At closing'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly}
          />
        </div>
      )}
      {properties[eFeePropertyNames.BuyerPaidBeforeClosing] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6 p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.BuyerPaidBeforeClosing}`}
            label={
              (!properties[eFeePropertyNames.BuyerPaidAtClosing] && (isRefinance ? 'Borrower Paid' : 'Buyer Paid')) || (
                <div>&nbsp;</div>
              )
            }
            hintText={'Before closing'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly}
          />
        </div>
      )}
      {properties[eFeePropertyNames.OthersPaidBuyer] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6 p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.OthersPaidBuyer}`}
            label={'Others Paid'}
            hintText={'For Buyer'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly}
          />
        </div>
      )}
      {properties[eFeePropertyNames.SendingToSpruceBuyer] && (
        <div className={'flex-shrink-0 flex-grow basis-1/2 p-1'}>
          <Field name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.OthersPaidBuyer}`}>
            {({ field }: FieldProps) => (
              <SelectField
                label={sendingToLabel}
                hintText={'For Buyer'}
                options={lenderPaidOptions(isZillowClosingEnabled)}
                name={`${baseName}.stringFeePropertyValues.${eFeePropertyNames.SendingToSpruceBuyer}`}
                isClearable={true}
                isDisabled={
                  readOnly ||
                  !properties[eFeePropertyNames.SendingToSpruceBuyer] ||
                  typeof field.value === 'undefined' ||
                  field.value <= 0
                }
              />
            )}
          </Field>
        </div>
      )}
    </>
  );
};
